import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'

import { Form, FormField } from '@/Core/UI/Form'
import { FormRow, FormSection } from '@/Ship/Components/Form/FormSection'
import {
    clusterCodeOf,
    regionFilterFor,
} from '@/Containers/AssignmentSection/AssignmentContainer/Helpers/clusterRegion'
import { iriOf, userUuidOf, vehicleFilterFor } from '../Helpers/driverVehicle'
import { writableShiftFields } from '../Helpers/writableFields'
import { LoadDriverCarrierTask } from '../Tasks/LoadDriverCarrierTask'

/**
 * Форма зміни (створення й правка диспетчером).
 * Каскади: «Кластер → Регіон» (як у задачах) і «Водій → ТЗ» (ТЗ перевізника водія;
 * власному водію — ТЗ власних перевізників). Справжня зміна кластера/водія очищає
 * залежне поле; перший запуск — ні, щоб не стерти значення картки при редагуванні.
 */
export const ShiftFormTemplate = observer(({ formContext, crudContext }: any) => {
    const status: string | undefined = formContext.getValue('status')

    // Кластер → Регіон
    const clusterCode = clusterCodeOf(formContext.getValue('cluster'))
    const prevClusterCode = useRef<string | null>(clusterCode)
    const clusterInitialised = useRef(false)

    useEffect(() => {
        formContext.getField('region')?.mergeInputProps({
            disabled: !clusterCode,
            filter: regionFilterFor(clusterCode),
        })
        if (clusterInitialised.current && prevClusterCode.current !== clusterCode) {
            formContext.setValue('region', null)
        }
        prevClusterCode.current = clusterCode
        clusterInitialised.current = true
    }, [formContext, clusterCode])

    // Водій → ТЗ: перевізника водія беремо з його картки (у значенні select-а його немає)
    const driverUuid = userUuidOf(formContext.getValue('driver'))
    const prevDriverUuid = useRef<string | null>(driverUuid)
    const driverInitialised = useRef(false)
    const [driverCarrier, setDriverCarrier] = useState<any | undefined>(undefined)

    useEffect(() => {
        let cancelled = false
        setDriverCarrier(undefined)
        if (driverUuid) {
            LoadDriverCarrierTask(driverUuid)
                .then((carrier) => {
                    if (!cancelled) setDriverCarrier(carrier)
                })
                .catch(() => {
                    // Без перевізника фільтр лишається базовим; бекенд усе одно перевірить пару
                    if (!cancelled) setDriverCarrier(undefined)
                })
        }
        if (driverInitialised.current && prevDriverUuid.current !== driverUuid) {
            formContext.setValue('vehicle', null)
        }
        prevDriverUuid.current = driverUuid
        driverInitialised.current = true

        return () => {
            cancelled = true
        }
    }, [formContext, driverUuid])

    const vehicleFilterKey = JSON.stringify(vehicleFilterFor(driverCarrier))
    useEffect(() => {
        formContext.getField('vehicle')?.mergeInputProps({
            disabled: !driverUuid,
            filter: JSON.parse(vehicleFilterKey),
        })
    }, [formContext, driverUuid, vehicleFilterKey])

    // Розрахункові та системні поля — лише для перегляду
    useEffect(() => {
        ;['durationMinutes', 'mileage', 'externalCode'].forEach((name) =>
            formContext.getField(name)?.mergeInputProps({ disabled: true })
        )
        // Код зміни з ERP не змінюємо: за ним ERP шле оновлення
        formContext.getField('code')?.mergeInputProps({ disabled: !!formContext.getValue('externalCode') })
    }, [formContext])

    // У запит — лише поля, дозволені для статусу; об'єкти довідників → IRI
    useEffect(() => {
        const eventManager = crudContext?.eventManager
        if (!eventManager) return undefined

        eventManager.on(
            'onBeforeSendSaveRequest',
            'stripNonWritableShiftFields',
            ({ values }: { values: Record<string, any> }) => {
                const writable = writableShiftFields(values['status'])
                Object.keys(values).forEach((key) => {
                    if (!writable.includes(key)) {
                        delete values[key]
                        return
                    }
                    if (values[key] && typeof values[key] === 'object' && values[key]['@id']) {
                        values[key] = iriOf(values[key])
                    }
                })
            }
        )

        return () => eventManager.off('onBeforeSendSaveRequest', 'stripNonWritableShiftFields')
    }, [crudContext])

    const showStart = status === 'started' || status === 'completed'
    const showComplete = status === 'completed'

    return (
        <Form formContext={formContext}>
            <FormSection title="Зміна">
                <FormRow>
                    <FormField name="code" />
                    <FormField name="shiftDate" />
                </FormRow>
                <FormRow>
                    <FormField name="driver" />
                    <FormField name="vehicle" />
                </FormRow>
                <FormRow>
                    <FormField name="cluster" />
                    <FormField name="region" />
                </FormRow>
            </FormSection>

            {showStart && (
                <FormSection title="Початок зміни">
                    <FormRow>
                        <FormField name="startedAt" />
                        <FormField name="startOdometer" />
                    </FormRow>
                    <FormField name="documentsConfirmed" />
                    <FormField name="safetyBriefingConfirmed" />
                </FormSection>
            )}

            {showComplete && (
                <FormSection title="Завершення зміни">
                    <FormRow>
                        <FormField name="completedAt" />
                        <FormField name="endOdometer" />
                    </FormRow>
                    <FormField name="technicalRemarks" />
                    <FormRow>
                        <FormField name="durationMinutes" />
                        <FormField name="mileage" />
                    </FormRow>
                    {/* Поки немає інтеграції з Wialon — вносить диспетчер; з ним рахується контроль пробігу */}
                    <FormField name="wialonMileage" />
                </FormSection>
            )}
        </Form>
    )
})
